import { memo } from 'react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import type { MiniNotchItem, MiniNotchSlot } from './notch-items'
import { notchEaseOut, notchEmergeFadeTransition, notchResizeTransition } from './notch-motion'

const notchItemTransition: Transition = {
  duration: 0.18,
  ease: notchEaseOut,
  layout: notchResizeTransition,
}

const leftItemEntranceTransition: Transition = {
  ...notchItemTransition,
  opacity: notchEmergeFadeTransition,
  filter: notchEmergeFadeTransition,
}

const notchItemVariants: Variants = {
  hidden: (slot: MiniNotchSlot) => ({
    opacity: 0,
    scale: 0.96,
    filter: 'blur(4px)',
    x: slot === 'left' ? 4 : slot === 'right' ? -4 : 0,
    y: slot === 'below' ? -4 : 0,
  }),
  visible: (slot: MiniNotchSlot) => ({
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    x: 0,
    y: 0,
    transition: slot === 'left' ? leftItemEntranceTransition : notchItemTransition,
  }),
}

interface NotchSlotListProps {
  items: Array<MiniNotchItem>
  slot: MiniNotchSlot
}

/**
 * Items are laid out at their natural width; the parent cutout owns all size animation, so this
 * list never animates its own bounds.
 */
export const NotchSlotList = memo(function NotchSlotList({ items, slot }: NotchSlotListProps) {
  return (
    <div
      className={twMerge(
        'relative flex items-center gap-0.5',
        slot === 'left' && 'flex-row-reverse',
        slot === 'center' && 'w-full px-0.5',
        slot === 'below' && items.length > 0 && 'w-full flex-col items-stretch p-1',
      )}
    >
      <AnimatePresence initial={false} mode="popLayout">
        {items.map((item) => (
          <motion.div
            key={item.id}
            layout={item.holdsPosition ? false : 'position'}
            custom={slot}
            variants={notchItemVariants}
            initial={item.hasOwnEntrance ? false : 'hidden'}
            animate="visible"
            exit="hidden"
            transition={notchItemTransition}
            className={item.className}
            style={item.style}
          >
            {item.node}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
})
